/**
 * 「今日话题」的展示辅助（顾问陪跑 P2-3）：把服务端算好的计划与话题卡描述成销售看得懂的话，
 * 并把一本书的卡片分成「今日推荐 / 其他话题 / 其他卡片」。纯函数，不依赖界面，便于单测。
 * 推荐本身（发几个、先发哪个）在服务端 advisor-today.ts 里算，这里只负责说明。
 */
import type { AdvisorCardDto, TodayPlanDto } from '../../../api/advisor';

const DAY_MS = 24 * 60 * 60 * 1000;
/** 中国时区，固定 UTC+8（与服务端口径一致，不随浏览器时区变） */
const TZ_OFFSET_MS = 8 * 60 * 60 * 1000;

export type Tone = 'muted' | 'warn' | 'danger' | 'success';
export interface PlanNote {
  text: string;
  tone: Tone;
}

/** 标题行：今日建议发几个、已发几个 */
export function planHeadline(plan: TodayPlanDto): string {
  if (plan.quota === 0) return '今日话题';
  return plan.sentToday > 0
    ? `今日话题 · 建议发 ${plan.quota} 个，已发 ${plan.sentToday} 个`
    : `今日话题 · 建议发 ${plan.quota} 个`;
}

/** 标题下面的说明：交付进度、客户多久没开口 */
export function planNotes(plan: TodayPlanDto): PlanNote[] {
  const notes: PlanNote[] = [];
  if (plan.remainingTopics === 0) {
    notes.push({ text: '所有话题都聊完了', tone: 'success' });
  } else if (plan.overdueDays !== null) {
    notes.push({ text: `已超过期望交付日 ${plan.overdueDays} 天，还有 ${plan.remainingQuestions} 道题没聊`, tone: 'danger' });
  } else if (plan.daysLeft !== null) {
    notes.push({ text: `距期望交付还有 ${plan.daysLeft} 天，还有 ${plan.remainingQuestions} 道题没聊`, tone: 'muted' });
  } else {
    notes.push({ text: `没填期望交付日期，按每天 1 个话题的节奏；还有 ${plan.remainingQuestions} 道题没聊`, tone: 'muted' });
  }
  if (plan.remainingTopics > 0) {
    if (plan.idleDays === null) notes.push({ text: '客户还没开始讲', tone: 'warn' });
    else if (plan.idleDays >= 3) notes.push({ text: `客户已经 ${plan.idleDays} 天没开口了，今天提醒一下`, tone: 'warn' });
  }
  return notes;
}

/** 话题卡上的「发过没有」：今天已发 / 昨天发过 / N 天前发过；没发过返回 null */
export function sentText(card: Pick<AdvisorCardDto, 'lastSentAt' | 'recommendation'>, now = Date.now()): string | null {
  if (card.recommendation?.sentToday) return '今天已发';
  if (card.lastSentAt == null) return null;
  const day = (ms: number) => Math.floor((ms + TZ_OFFSET_MS) / DAY_MS);
  const days = day(now) - day(card.lastSentAt);
  if (days <= 0) return '今天已发';
  return days === 1 ? '昨天发过' : `${days} 天前发过`;
}

export interface BookCards {
  /** 今日推荐的话题卡，按推荐次序 */
  recommended: AdvisorCardDto[];
  /** 其余话题卡，保持大纲顺序 */
  otherTopics: AdvisorCardDto[];
  /** 订单 / 定稿 / 阅读等非话题卡 */
  rest: AdvisorCardDto[];
}

export function splitTodayCards(cards: AdvisorCardDto[]): BookCards {
  const topics = cards.filter((c) => c.type === 'topic');
  return {
    recommended: topics.filter((c) => c.recommendation).sort((a, b) => a.recommendation!.rank - b.recommendation!.rank),
    otherTopics: topics.filter((c) => !c.recommendation),
    rest: cards.filter((c) => c.type !== 'topic'),
  };
}

/** 一组卡片里涉及的话题卡所属的书（按出现先后，去重） */
export function topicWorkIds(cards: AdvisorCardDto[]): string[] {
  return [...new Set(cards.filter((c) => c.type === 'topic' && c.workId).map((c) => c.workId as string))];
}
